import React, { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import Accordion from 'react-bootstrap/Accordion';
import cx from 'classnames';
import _ from 'lodash';
import userRoleService from 'src/utils/userRoles.service';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { logoutUser } from 'src/redux/actions/users';
import { setChromeExtAlertBannerVisible, setSkipSearchAnimation } from 'src/utils/util';

interface SettingItem {
  name: string;
  url: string;
  visible: () => boolean;
  children?: SettingItem[];
  icon?: string;
}


const SettingsSideMenu: React.FC = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [activeKey, setActiveKey] = useState<any>("");

  // check if user can access any account settings, then show account settings menu
  const canAccessAccountSettings = () =>  _.some([ 
      userRoleService.canAccessAccountManagement(),
      userRoleService.canAccessUserManagement(),
      userRoleService.canAccessPromoteSettings(), 
      userRoleService.canCloneHub() && userRoleService.canEditLayout(),
      userRoleService.canAccessCustomFields(),
      userRoleService.canAccessPrivacyPolicyControl(),
      userRoleService.canAccessPaymentHistory()
  ]);

  const settingsConfig: SettingItem[] = [
    {
      name: "Dashboard",
      url: '/settings/dashboard',
      visible: () => userRoleService.isAdmin(),
      icon: "paperflite-dashboard"
    },
    {
      name: "Profile",
      url: '/settings/profile',
      visible: () => true,
      icon: "paperflite-profile"
    },
    {
      name: "Notifications",
      url: '/settings/notifications',
      visible: () => userRoleService.canAccessNotifications(),
      icon: "paperflite-notifications"
    },
    {
      name: "Account",
      url: '/settings/account-management',
      visible: () => canAccessAccountSettings(),
      icon: "paperflite-account",
      children: [
        {
          name: "Account Settings",
          url: '/settings/account-management',
          visible: () => userRoleService.canAccessAccountManagement(),

        },
        {
          name: "User Management",
          url: '/settings/user-management',
          visible: () => userRoleService.canAccessUserManagement(),

        },
        {
          name: "Viewer Settings",
          url: '/settings/fliteview',
          visible: () => userRoleService.canAccessPromoteSettings(),

        },
        {
          name: "Hub Settings",
          url: '/settings/hub-settings',
          visible: () =>
            userRoleService.canCloneHub() &&
            userRoleService.canEditLayout(),

        },
        {
          name: "Custom Fields",
          url: '/settings/custom_fields',
          visible: () => userRoleService.canAccessCustomFields(),

        },
        {
          name: "Privacy Policy Control",
          url: '/settings/privacy-policy-control',
          visible: () =>
            userRoleService.canAccessPrivacyPolicyControl(),

        },
        {
          name: "Payment History",
          url: '/settings/payment-history',
          visible: () => userRoleService.canAccessPaymentHistory(),

        },
      ],
    },
    {
      name: "Stream Access Management",
      url: '/settings/stream-access-management',
      visible: () =>
        userRoleService.canAccessHub() &&
        userRoleService.canCreateStream(),
      icon: "paperflite-stream-access"
    },
    {
      name: "Distribution List",
      url: '/settings/distribution_lists',
      visible: () => userRoleService.canAccessDistributionLists(),
      icon: "paperflite-distribution-list"
    },
    {
      name: "Content Sources",
      url: '/settings/data-sources',
      visible: () => userRoleService.canAccessDataSource(),
      icon: "paperflite-content-sources"
    },
    {
      name: "Integrations",
      url: '/settings/integrations',
      visible: () => userRoleService.canAccessIntegrations(),
      icon: "paperflite-integrations"
    },
    {
      name: "Templates",
      url: '/settings/email-template',
      visible: () => userRoleService.canShare(),
      icon: "paperflite-template",
      children: [
        {
          name: "Email Templates",
          url: '/settings/email-template',
          visible: () => userRoleService.canAccessEmailTemplates(),

        },
        {
          name: "NDA Templates",
          url: '/settings/nda-templates',
          visible: () => userRoleService.canAccessNdaTemplates(),

        },
      ],
    },
    {
      name: 'Hey Sales',
      url: '/settings/skills',
      visible: () =>
        userRoleService.canAccessCoaching() &&
        userRoleService.canCreateCourse(),
      icon: "paperflite-group",
      children: [
        {
          name: 'Skills',
          url: '/settings/skills',
          visible: () =>
            userRoleService.canAccessCoaching() &&
            userRoleService.canCreateCourse(),

        },
        {
          name: 'Call Assessment',
          url: '/settings/call-assessments',
          visible: () =>
            userRoleService.canAccessCoaching() &&
            userRoleService.canCreateCourse()
        },
        {
          name: 'Gamification',
          url: '/settings/gamification',
          visible: () =>
            userRoleService.canAccessCoaching() &&
            userRoleService.canCreateCourse() && 
            userRoleService.canAccessGamification()
        }
      ],
    },
  ];

  const handleBackClick = () => {
    navigate( userRoleService.getHomePagePath());
  };

  const handleNavigateClick = (url: string) => {
    if (url) {
      if (url === "/logout") {
        setChromeExtAlertBannerVisible(true); // set chrome ext alert to true on logout
        setSkipSearchAnimation(false); // set global search intro animation to false on logout
        queryClient.clear();
        dispatch(logoutUser());
      }
      navigate(url);
    }
  };

  return (
    <aside className={styles.sideMenu}>
      <div className={styles.header}>
        <h1 className={styles.title}>  <i className={cx("paperflite-arrow-up", styles.backArrow)} onClick={handleBackClick} /> settings</h1>
        <p className={styles.subtitle}>step up, set up and done!</p>
      </div>

      <Accordion activeKey={activeKey} onSelect={(key) => setActiveKey(key)} className={styles.settingsMenu} alwaysOpen>
        {settingsConfig
          .filter(item => item.visible())
          .map((item, index) => {
            const hasChildren =
              item.children && item.children.some(child => child.visible());
            return (
              <Accordion.Item
                eventKey={index.toString()}
                key={index}
                className={cx({
                  [styles.hasChildren]: hasChildren,
                })}
              >
                <Accordion.Header>
                  <span onClick={() => handleNavigateClick(item.url)} className={cx(styles.menuItem, {
                    [styles.active]: pathname.includes(item.url) || (hasChildren && _.get(item, "children", []).some(child => pathname.includes(child.url)))
                  })}>
                    <i className={cx(item.icon, styles.menuIcon)} />
                    <span className={cx(styles.label)}>{item.name} {_.size(item.children) > 0 && <i className={cx("paperflite-dropdown", styles.dropDown, {
                      [styles.active]: _.includes(activeKey, index.toString())
                    })} />}</span>

                  </span>
                </Accordion.Header>
                {hasChildren && (
                  <Accordion.Body className={styles.accordionBody}>
                    {_.size(item.children) > 0 && _.get(item, 'children', []).filter(child => child.visible()).map((child, idx) => (
                      <span onClick={() => handleNavigateClick(child.url)} className={cx(styles.menuItem, styles.childMenuItem, {
                        [styles.active]: pathname.includes(child.url)
                      })} key={idx}>
                        <span className={cx(styles.label)}>{child.name}</span>
                      </span>
                    ))}

                  </Accordion.Body>
                )}
              </Accordion.Item>
            );
          })}
      </Accordion>

      <div className={styles.footer}>
        <a href="https://www.paperflite.com"
          target="_blank" rel="noreferrer" className={styles.footerMenu}>About Paperflite</a>
        <span onClick={() => handleNavigateClick("/settings/privacy-policy")} className={cx(styles.footerMenu, {
          [styles.active]: pathname.includes("/settings/privacy-policy")
        })}>Privacy policy</span>
        <span onClick={() => handleNavigateClick("/settings/terms-and-conditions")} className={cx(styles.footerMenu, {
          [styles.active]: pathname.includes("/settings/terms-and-conditions")
        })}>Legal & About</span>

        <div className={styles.menuItem}>
          <span className={styles.icon}><i className='paperflite-upload' /></span>
          <span onClick={() => handleNavigateClick("/logout")} className={styles.label}>Logout</span>
        </div>
      </div>
    </aside>
  );
};

export default SettingsSideMenu;
